import React from "react";

import { css } from "@emotion/react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { ImageType } from "@swo/storefront";
import { CSS_MQ } from "../../styles/breakpoints";

import cn from "classnames";
import { ContentfulImage, ContentfulImageProps } from "../_shared-components/image-related/ContentfulImage";
import { FONTS } from "../../styles/fonts";
import { ImageAspectRatio } from "../_shared-components/image-related/AspectRatio";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { CSS_COLORS } from "../../styles/styles-css-variables";

export const ImageComponent: ReactTFC<ImageType> = props => {
  const sizeCssClass = `width-${props.width}`;
  const imageCssClass = `image-${props.variant}`;

  function getAspectRatio(): { width: number; height: number } | undefined {
    if (typeof props.image.height === "number" && typeof props.image.width === "number" && props.image.height > 0) {
      return { width: props.image.width, height: props.image.height };
    } else {
      return undefined;
    }
  }

  const aspectRatioIfAvailable = getAspectRatio();

  const commonProps: ContentfulImageProps = {
    css: imageStyles,
    contentfulLink: {
      url: props.image.url,
      requestedWidth: 2000,
    },
    lazyLoaded: "lazy",
    higherDPI: [0.25, 0.5, 1.5],
    sizes: `${CSS_MQ.atMostSm} 100vw, ${props.width === "content" ? "1400px" : "100vw"}`,
    alt: props.image.description || props.image.title,
    assetId: props.image.sys.id,
  };

  return (
    <div
      className={cn(sizeCssClass, imageCssClass, getComponentCMSClassname(props?.sys?.id))}
      css={imageComponentStyles}
      style={{ "--image-width": aspectRatioIfAvailable ? "100%" : `${props.image.width}px` } as React.CSSProperties}
    >
      {(props.descriptiveHeadline || props.emotionalHeadline) && (
        <div css={headlineWrapperStyles}>
          {props.descriptiveHeadline && <span css={seoHeadlineStyles}>{props.descriptiveHeadline}</span>}
          {props.emotionalHeadline && <span css={emotionalHeadlineStyles}>{props.emotionalHeadline}</span>}
        </div>
      )}

      {aspectRatioIfAvailable ? (
        <ImageAspectRatio {...commonProps} aspectRatio={aspectRatioIfAvailable} />
      ) : (
        <ContentfulImage {...commonProps} />
      )}
    </div>
  );
};

const headlineWrapperStyles = css`
  --b: 25px;
  ${CSS_MQ.atLeastMd} {
    --b: 50px;
  }

  position: absolute;
  z-index: 2;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;

  width: 100%;

  height: 100%;

  bottom: var(--b);
  left: 0;

  &:before {
    content: "";
    position: absolute;

    width: 100%;

    max-width: var(--image-width);
    .image-fill & {
      max-width: 100%;
    }

    height: 150px;

    bottom: calc(-1 * var(--b));

    background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.7));
  }

  > * {
    position: relative;
  }
`;

const seoHeadlineStyles = css`
  ${FONTS.SwarovskiFutura500};

  color: ${CSS_COLORS.White};

  font-size: 16px;
  letter-spacing: 0.8px;
  ${CSS_MQ.atLeastMd} {
    font-size: 24px;
    letter-spacing: 1.2px;
  }

  max-width: calc(var(--image-width) - 20px);
  text-align: center;
`;

const emotionalHeadlineStyles = css`
  ${FONTS.MinervaModern500};
  color: ${CSS_COLORS.White};

  font-size: 30px;
  letter-spacing: 1px;
  line-height: 28.8px;

  ${CSS_MQ.atLeastMd} {
    font-size: 40px;
    letter-spacing: 2px;
    line-height: 42px;
  }

  ${CSS_MQ.atLeastLg} {
    font-size: 60px;
    letter-spacing: 3px;
    line-height: 56px;
  }

  text-transform: uppercase;

  max-width: calc(var(--image-width) - 20px);
  text-align: center;
`;

const imageStyles = css`
  max-width: 100%;

  display: block;

  .image-fill & {
    width: 100%;
  }

  .image-left & {
    margin-left: 0;
    margin-right: auto;
  }

  .image-center & {
    margin-left: auto;
    margin-right: auto;
  }

  .image-right & {
    margin-right: 0;
    margin-left: auto;
  }
`;

const imageComponentStyles = css`
  position: relative;

  &.width-full {
    width: 100%;
  }

  &.width-content {
    width: 100%;
    max-width: var(--max-content-width-new);
    margin-left: auto;
    margin-right: auto;
  }
`;
